import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
    forceCenter,
    forceCollide,
    forceLink,
    forceManyBody,
    forceRadial,
    forceSimulation,
    type Simulation,
    type SimulationLinkDatum,
    type SimulationNodeDatum,
} from 'd3-force';
import { select } from 'd3-selection';
import 'd3-transition';
import { zoom as d3Zoom, zoomIdentity, type ZoomBehavior } from 'd3-zoom';
import type { GraphIndex, KuralGraphEntry, PaalSlug } from '../data/types';
import type { Lang } from '../hooks/useLang';
import './FocusGraph.css';

// --------------------- types --------------------- //

type NodeKind = 'focus' | 'sibling' | 'related' | 'theme';

interface SimNode extends SimulationNodeDatum {
    id: string;
    kind: NodeKind;
    // Kural-only:
    kural_n?: number;
    paal?: PaalSlug;
    first_line_ta?: string;
    verse_ta?: string[];
    gist_en?: string;
    // Theme-only:
    slug?: string;
    title_en?: string;
    title_ta?: string;
}

interface SimEdge extends SimulationLinkDatum<SimNode> {
    id: string;
    kind: 'structural' | 'thematic' | 'theme-tag';
    weight: number;
    shared_tags?: string[];
}

interface Props {
    focus: KuralGraphEntry;
    graph: GraphIndex;
    lang: Lang;
    onNavigate: (target: { kind: 'kural' | 'theme'; id: number | string }) => void;
}

// --------------------- geometry constants --------------------- //

const RING_SIBLING = 115;
const RING_RELATED = 205;
const RING_THEME = 290;

const R_FOCUS = 15;
const R_KURAL = 8;
const R_KURAL_RELATED = 9;
const THEME_W = 82;
const THEME_H = 24;

const VIEW = { w: 820, h: 680 };

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 4;

// --------------------- helpers --------------------- //

// Thematic-edge weight scale. Structural edges are 1 px; every thematic
// edge is at least 2 px so a single-tag connection is already visibly
// heavier than a same-chapter hairline.
function edgeWidth(sharedCount: number): number {
    if (sharedCount <= 1) return 2;
    if (sharedCount === 2) return 3;
    if (sharedCount === 3) return 4;
    return 5;
}

/**
 * Distance from a node's centre to the point where an edge leaving in
 * the direction (dx, dy) exits the node's visible boundary. Used to
 * inset edge endpoints so lines terminate cleanly at the node's edge
 * instead of piercing through it into the centre.
 *
 * For kural circles this is just the radius (plus stroke half-width).
 * For theme pills it's a proper line-rectangle intersection so the
 * edge lands on the pill's side, not its centre.
 */
function nodeInset(kind: NodeKind, dx: number, dy: number): number {
    if (kind === 'focus') return R_FOCUS + 2;
    if (kind === 'sibling') return R_KURAL + 1;
    if (kind === 'related') return R_KURAL_RELATED + 1;
    // Theme pill: intersect the ray with the pill's bounding rect.
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    if (absDx === 0 && absDy === 0) return THEME_H / 2;
    const halfW = THEME_W / 2;
    const halfH = THEME_H / 2;
    const tx = absDx > 0 ? halfW / absDx : Infinity;
    const ty = absDy > 0 ? halfH / absDy : Infinity;
    const t = Math.min(tx, ty);
    return Math.hypot(t * dx, t * dy);
}

function truncate(s: string, n: number): string {
    return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

// Build the visible node & edge sets for a given focus.
function buildGraph(
    focus: KuralGraphEntry,
    graph: GraphIndex
): { nodes: SimNode[]; edges: SimEdge[] } {
    const adhigaram = graph.adhigarams.find((a) => a.n === focus.adhigaram_n);
    const siblingNs = adhigaram
        ? adhigaram.kural_ns.filter((n) => n !== focus.n)
        : [];
    const themeBySlug = new Map(graph.themes.map((t) => [t.slug, t]));
    const kuralByN = new Map(graph.kurals.map((k) => [k.n, k]));

    const nodes: SimNode[] = [];
    const edges: SimEdge[] = [];

    // Focus (paal-coloured, same as siblings — they're in the same chapter).
    nodes.push({
        id: `k-${focus.n}`,
        kind: 'focus',
        kural_n: focus.n,
        paal: focus.paal,
        first_line_ta: focus.first_line_ta,
        gist_en: focus.gist_en,
    });

    // Siblings (Ring 1) — share the focus's paal by construction.
    for (const n of siblingNs) {
        const k = kuralByN.get(n);
        if (!k) continue;
        nodes.push({
            id: `k-${n}`,
            kind: 'sibling',
            kural_n: n,
            paal: k.paal,
            first_line_ta: k.first_line_ta,
            gist_en: k.gist_en,
        });
        edges.push({
            id: `s-${focus.n}-${n}`,
            kind: 'structural',
            source: `k-${focus.n}`,
            target: `k-${n}`,
            weight: 1,
        });
    }

    // Related (Ring 2) — may be from any paal; coloured by their own.
    for (const rel of focus.related) {
        // If a related kural is also a sibling, upgrade to thematic ring.
        const existing = nodes.find((n) => n.kural_n === rel.n);
        if (!existing) {
            const k = kuralByN.get(rel.n);
            if (!k) continue;
            nodes.push({
                id: `k-${rel.n}`,
                kind: 'related',
                kural_n: rel.n,
                paal: k.paal,
                first_line_ta: k.first_line_ta,
                verse_ta: k.verse_ta,
                gist_en: k.gist_en,
            });
        }
        edges.push({
            id: `t-${focus.n}-${rel.n}`,
            kind: 'thematic',
            source: `k-${focus.n}`,
            target: `k-${rel.n}`,
            weight: edgeWidth(rel.shared_tags.length),
            shared_tags: rel.shared_tags,
        });
    }

    // Themes (outer ring).
    for (const slug of focus.tags) {
        const t = themeBySlug.get(slug);
        if (!t) continue;
        nodes.push({
            id: `t-${slug}`,
            kind: 'theme',
            slug,
            title_en: t.title_en,
            title_ta: t.title_ta,
        });
        edges.push({
            id: `tt-${focus.n}-${slug}`,
            kind: 'theme-tag',
            source: `k-${focus.n}`,
            target: `t-${slug}`,
            weight: 1,
        });
    }

    return { nodes, edges };
}

// --------------------- component --------------------- //

interface HoverPayload {
    node?: SimNode;
    edge?: SimEdge;
    clientX: number;
    clientY: number;
}

export default function FocusGraph({ focus, graph, lang, onNavigate }: Props) {
    const svgRef = useRef<SVGSVGElement>(null);
    const contentRef = useRef<SVGGElement>(null);
    const nodesRef = useRef<SimNode[]>([]);
    const simRef = useRef<Simulation<SimNode, SimEdge> | null>(null);
    const zoomRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);

    const [positions, setPositions] = useState<
        Map<string, { x: number; y: number }>
    >(new Map());
    const [hover, setHover] = useState<HoverPayload | null>(null);
    const [ready, setReady] = useState(false);
    // Touch devices don't fire hover — we surface the same tooltip via
    // a two-tap pattern: first tap previews the node, second tap on
    // the same node navigates. Tracked separately from ``hover`` so
    // desktop mouse behaviour stays exactly as it was.
    const [isTouch, setIsTouch] = useState(false);
    const [previewId, setPreviewId] = useState<string | null>(null);

    const { nodes, edges } = useMemo(
        () => buildGraph(focus, graph),
        [focus, graph]
    );

    // ---- force simulation ---- //
    // useLayoutEffect (not useEffect) so the pre-settle + first paint
    // commit BEFORE the browser paints, eliminating the flash of the
    // initial random-cloud state.
    useLayoutEffect(() => {
        const prevById = new Map(nodesRef.current.map((n) => [n.id, n]));

        const prevFocus = nodesRef.current.find((n) => n.kind === 'focus');
        const fallbackX = prevFocus?.x ?? 0;
        const fallbackY = prevFocus?.y ?? 0;

        for (const n of nodes) {
            const prior = prevById.get(n.id);
            if (prior) {
                n.x = prior.x;
                n.y = prior.y;
                // Zero velocities on every entry so any later pre-tick
                // starts from rest — no residual momentum can drift the
                // graph after we paint the settled state.
                n.vx = 0;
                n.vy = 0;
            } else {
                n.x = fallbackX + (Math.random() - 0.5) * 20;
                n.y = fallbackY + (Math.random() - 0.5) * 20;
            }
        }

        const focusNode = nodes.find((n) => n.kind === 'focus')!;
        if (!prevById.has(focusNode.id)) {
            focusNode.x = 0;
            focusNode.y = 0;
        }
        focusNode.fx = 0;
        focusNode.fy = 0;

        nodesRef.current = nodes;

        if (simRef.current) {
            simRef.current.stop();
        }

        const linkForce = forceLink<SimNode, SimEdge>(edges)
            .id((n) => n.id)
            .distance((e) => {
                if (e.kind === 'structural') return RING_SIBLING;
                if (e.kind === 'thematic') return RING_RELATED;
                return RING_THEME;
            })
            .strength(0.35);

        const sim = forceSimulation<SimNode, SimEdge>(nodes)
            .force('link', linkForce)
            .force('charge', forceManyBody<SimNode>().strength(-160))
            .force('centre', forceCenter(0, 0).strength(0.02))
            .force(
                'collide',
                forceCollide<SimNode>().radius((n) => {
                    if (n.kind === 'theme') return THEME_W / 2 + 4;
                    if (n.kind === 'focus') return R_FOCUS + 6;
                    return R_KURAL_RELATED + 4;
                })
            )
            .force(
                'ring-sibling',
                forceRadial<SimNode>(RING_SIBLING, 0, 0).strength((n) =>
                    n.kind === 'sibling' ? 0.45 : 0
                )
            )
            .force(
                'ring-related',
                forceRadial<SimNode>(RING_RELATED, 0, 0).strength((n) =>
                    n.kind === 'related' ? 0.45 : 0
                )
            )
            .force(
                'ring-theme',
                forceRadial<SimNode>(RING_THEME, 0, 0).strength((n) =>
                    n.kind === 'theme' ? 0.55 : 0
                )
            )
            .alpha(1)
            .alphaTarget(0)
            .alphaDecay(0.06)
            .velocityDecay(0.65);

        const paint = () => {
            const map = new Map<string, { x: number; y: number }>();
            for (const n of nodes) map.set(n.id, { x: n.x ?? 0, y: n.y ?? 0 });
            setPositions(map);
        };

        // Every render pre-settles the sim synchronously and paints
        // once. No tick handler is ever attached — the graph is fully
        // static after paint, so the shared-theme dashed edges and
        // theme dotted-tags stay pinned exactly where they land. On
        // refocus this means the new neighbourhood snaps into place
        // instantly rather than gliding, but that trades animation
        // polish for rock-steady positioning.
        sim.stop();
        sim.tick(200);
        paint();

        simRef.current = sim;
        // Reveal almost immediately — nothing to hide since the graph
        // is already in position by the time we paint.
        const readyTimer = window.setTimeout(() => setReady(true), 40);
        return () => {
            window.clearTimeout(readyTimer);
            sim.stop();
        };
    }, [nodes, edges]);

    // ---- zoom / pan ---- //
    useEffect(() => {
        const svg = svgRef.current;
        const content = contentRef.current;
        if (!svg || !content) return;

        const zoomBehavior = d3Zoom<SVGSVGElement, unknown>()
            .scaleExtent([ZOOM_MIN, ZOOM_MAX])
            .filter((event: Event) => {
                // Block pan when starting on an interactive node / theme pill;
                // allow it on empty space or edges.
                const target = event.target as SVGElement | null;
                if (
                    event.type === 'mousedown' &&
                    target?.closest('.focus-graph__node')
                ) {
                    return false;
                }
                return !('button' in event) || (event as MouseEvent).button === 0;
            })
            .on('zoom', (event) => {
                content.setAttribute('transform', event.transform.toString());
            });

        zoomRef.current = zoomBehavior;
        select(svg).call(zoomBehavior);
        select(svg).call(zoomBehavior.transform, zoomIdentity);

        return () => {
            select(svg).on('.zoom', null);
        };
    }, [focus.n]);

    // ---- interaction handlers ---- //

    const onEnter = useCallback(
        (kind: 'node' | 'edge', payload: SimNode | SimEdge) =>
            (e: React.MouseEvent) => {
                setHover({
                    [kind === 'node' ? 'node' : 'edge']: payload,
                    clientX: e.clientX,
                    clientY: e.clientY,
                } as HoverPayload);
            },
        []
    );
    const onMove = useCallback((e: React.MouseEvent) => {
        setHover((h) =>
            h ? { ...h, clientX: e.clientX, clientY: e.clientY } : h
        );
    }, []);
    const onLeave = useCallback(() => setHover(null), []);

    const onNodeClick = useCallback(
        (n: SimNode) => (e: React.MouseEvent) => {
            if (n.kind === 'focus') return;
            // Touch two-tap: first tap on a fresh node just previews
            // the tooltip, without navigating. Second tap on the same
            // node (previewId already matches) falls through to nav.
            if (isTouch && previewId !== n.id) {
                setPreviewId(n.id);
                setHover({
                    node: n,
                    clientX: e.clientX,
                    clientY: e.clientY,
                });
                return;
            }
            setPreviewId(null);
            setHover(null);
            if (n.kind === 'theme' && n.slug) {
                onNavigate({ kind: 'theme', id: n.slug });
            } else if (n.kural_n != null) {
                onNavigate({ kind: 'kural', id: n.kural_n });
            }
        },
        [onNavigate, isTouch, previewId]
    );

    // Dismiss the touch preview when the user taps an empty area of
    // the SVG (background/edges that aren't thematic). Node/edge
    // click handlers run first and set their own preview, so this
    // only fires for genuinely empty taps.
    const onSvgClick = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
        const target = e.target as Element;
        if (
            target.closest('.focus-graph__node') ||
            target.closest('.focus-graph__edge--thematic')
        ) {
            return;
        }
        setPreviewId(null);
        setHover(null);
    }, []);

    const resetView = useCallback(() => {
        const svg = svgRef.current;
        const zoomBehavior = zoomRef.current;
        if (!svg || !zoomBehavior) return;
        select(svg)
            .transition()
            .duration(400)
            .call(zoomBehavior.transform, zoomIdentity);
    }, []);

    // ---- touch detection ---- //
    // Use ``(hover: none)`` rather than a UA sniff — this is what CSS
    // media queries use to gate hover-only styling, so React and CSS
    // stay in agreement.
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mq = window.matchMedia('(hover: none)');
        setIsTouch(mq.matches);
        const listener = (e: MediaQueryListEvent) => setIsTouch(e.matches);
        mq.addEventListener('change', listener);
        return () => mq.removeEventListener('change', listener);
    }, []);

    // Keep ``previewId`` and ``hover`` in sync: whenever hover clears
    // (mouseleave on desktop, outside-tap on mobile), the preview
    // should reset too so the next tap re-enters preview mode.
    useEffect(() => {
        if (!hover) setPreviewId(null);
    }, [hover]);

    // On touch, tapping outside the graph container dismisses the
    // preview tooltip. On desktop we skip this — mouseleave already
    // handles it.
    useEffect(() => {
        if (!isTouch || (!hover && !previewId)) return;
        const container = svgRef.current?.parentElement;
        if (!container) return;
        const onDocPointerDown = (e: PointerEvent) => {
            if (!container.contains(e.target as Node)) {
                setPreviewId(null);
                setHover(null);
            }
        };
        document.addEventListener('pointerdown', onDocPointerDown);
        return () => document.removeEventListener('pointerdown', onDocPointerDown);
    }, [isTouch, hover, previewId]);

    return (
        <div className="focus-graph">
            <svg
                ref={svgRef}
                className={`focus-graph__svg${ready ? ' is-ready' : ''}`}
                viewBox={`${-VIEW.w / 2} ${-VIEW.h / 2} ${VIEW.w} ${VIEW.h}`}
                preserveAspectRatio="xMidYMid meet"
                role="img"
                aria-label={`Graph of Kural ${focus.n} and its connections — drag to pan, scroll to zoom`}
                onClick={onSvgClick}
            >
                <g ref={contentRef}>
                    {/* --- edges --- */}
                    <g className="focus-graph__edges">
                        {edges.map((e) => {
                            // After the sim initialises, ``source`` and ``target``
                            // become SimNode references; earlier they're string IDs.
                            const sNode =
                                typeof e.source === 'string'
                                    ? nodes.find((n) => n.id === e.source)
                                    : (e.source as SimNode);
                            const tNode =
                                typeof e.target === 'string'
                                    ? nodes.find((n) => n.id === e.target)
                                    : (e.target as SimNode);
                            if (!sNode || !tNode) return null;
                            const s = positions.get(sNode.id);
                            const t = positions.get(tNode.id);
                            if (!s || !t) return null;

                            // Inset each endpoint by the node's radius so the
                            // line terminates cleanly at the node boundary
                            // instead of piercing through into the centre.
                            const dx = t.x - s.x;
                            const dy = t.y - s.y;
                            const dist = Math.hypot(dx, dy);
                            if (dist === 0) return null;
                            const sInset = nodeInset(sNode.kind, -dx, -dy);
                            const tInset = nodeInset(tNode.kind, dx, dy);
                            if (sInset + tInset >= dist) return null;
                            const ux = dx / dist;
                            const uy = dy / dist;
                            const x1 = s.x + ux * sInset;
                            const y1 = s.y + uy * sInset;
                            const x2 = t.x - ux * tInset;
                            const y2 = t.y - uy * tInset;

                            const cls =
                                e.kind === 'structural'
                                    ? 'focus-graph__edge focus-graph__edge--structural'
                                    : e.kind === 'thematic'
                                        ? 'focus-graph__edge focus-graph__edge--thematic'
                                        : 'focus-graph__edge focus-graph__edge--theme-tag';
                            const isThematic = e.kind === 'thematic';
                            return (
                                <line
                                    key={e.id}
                                    x1={x1}
                                    y1={y1}
                                    x2={x2}
                                    y2={y2}
                                    className={cls}
                                    strokeWidth={e.weight}
                                    onMouseEnter={isThematic ? onEnter('edge', e) : undefined}
                                    onMouseMove={isThematic ? onMove : undefined}
                                    onMouseLeave={isThematic ? onLeave : undefined}
                                />
                            );
                        })}
                    </g>

                    {/* --- nodes --- */}
                    <g className="focus-graph__nodes">
                        {nodes.map((n) => {
                            const pos = positions.get(n.id);
                            if (!pos) return null;

                            if (n.kind === 'theme') {
                                const label = truncate(
                                    lang === 'ta' ? n.title_ta ?? '' : n.title_en ?? '',
                                    14
                                );
                                return (
                                    <g
                                        key={n.id}
                                        transform={`translate(${pos.x} ${pos.y})`}
                                        className="focus-graph__node focus-graph__node--theme"
                                        tabIndex={-1}
                                        onClick={onNodeClick(n)}
                                        onMouseEnter={onEnter('node', n)}
                                        onMouseMove={onMove}
                                        onMouseLeave={onLeave}
                                    >
                                        <rect
                                            x={-THEME_W / 2}
                                            y={-THEME_H / 2}
                                            width={THEME_W}
                                            height={THEME_H}
                                            rx={THEME_H / 2}
                                            ry={THEME_H / 2}
                                            className="focus-graph__theme-bg"
                                        />
                                        <text
                                            x={0}
                                            y={4}
                                            textAnchor="middle"
                                            className="focus-graph__theme-label"
                                            {...(lang === 'ta' ? { 'data-lang': 'ta' } : {})}
                                        >
                                            {label}
                                        </text>
                                    </g>
                                );
                            }

                            const isFocus = n.kind === 'focus';
                            const r =
                                n.kind === 'focus'
                                    ? R_FOCUS
                                    : n.kind === 'related'
                                        ? R_KURAL_RELATED
                                        : R_KURAL;
                            const kindClass =
                                n.kind === 'focus'
                                    ? 'focus-graph__node--focus'
                                    : n.kind === 'related'
                                        ? 'focus-graph__node--related'
                                        : 'focus-graph__node--sibling';
                            const paalClass = n.paal
                                ? `focus-graph__node--paal-${n.paal}`
                                : '';
                            return (
                                <g
                                    key={n.id}
                                    transform={`translate(${pos.x} ${pos.y})`}
                                    className={`focus-graph__node ${kindClass} ${paalClass}`}
                                    tabIndex={-1}
                                    onClick={onNodeClick(n)}
                                    onMouseEnter={onEnter('node', n)}
                                    onMouseMove={onMove}
                                    onMouseLeave={onLeave}
                                >
                                    <circle r={r} className="focus-graph__kural-bg" />
                                    {isFocus && (
                                        <text
                                            y={4}
                                            textAnchor="middle"
                                            className="focus-graph__kural-label"
                                        >
                                            {n.kural_n}
                                        </text>
                                    )}
                                </g>
                            );
                        })}
                    </g>
                </g>
            </svg>

            {/* --- legend (translucent card) --- */}
            <div className="focus-graph__legend" aria-label="Graph legend">
                <ul className="focus-graph__legend-list">
                    <li>
                        <span
                            className="focus-graph__swatch focus-graph__swatch--focus"
                            aria-hidden="true"
                        />
                        focused kural
                    </li>
                    <li>
                        <span
                            className="focus-graph__swatch focus-graph__swatch--sibling"
                            aria-hidden="true"
                        />
                        same chapter
                    </li>
                    <li>
                        <span
                            className="focus-graph__swatch focus-graph__swatch--related"
                            aria-hidden="true"
                        />
                        shared theme
                    </li>
                    <li>
                        <span
                            className="focus-graph__swatch focus-graph__swatch--theme-pill"
                            aria-hidden="true"
                        />
                        themes
                    </li>
                </ul>
            </div>

            {/* --- controls --- */}
            <div className="focus-graph__controls">
                <button
                    type="button"
                    className="focus-graph__reset"
                    onClick={resetView}
                    aria-label="Reset zoom and pan"
                    title="Reset view"
                >
                    Reset view
                </button>
                <div className="focus-graph__hint" aria-hidden="true">
                    scroll to zoom · drag to pan
                </div>
            </div>

            {/* --- tooltip (follows mouse on desktop, docked on touch) --- */}
            {hover && (hover.node || hover.edge) && (
                <TooltipCard
                    payload={hover}
                    graph={graph}
                    lang={lang}
                    isTouch={isTouch}
                />
            )}
        </div>
    );
}

// --------------------- tooltip --------------------- //

function TooltipCard({
    payload,
    graph,
    lang,
    isTouch,
}: {
    payload: HoverPayload;
    graph: GraphIndex;
    lang: Lang;
    isTouch: boolean;
}) {
    // On desktop the tooltip follows the mouse; on touch it docks at
    // the bottom of the viewport (via CSS keyed off ``data-touch``)
    // so it can't fall off-screen on small displays.
    const positionStyle: React.CSSProperties | undefined = isTouch
        ? undefined
        : { left: payload.clientX, top: payload.clientY };
    const touchAttr = isTouch ? { 'data-touch': 'true' } : {};

    if (payload.node) {
        const n = payload.node;
        const canNavigate = n.kind !== 'focus';
        const hint = isTouch && canNavigate ? (
            <div className="focus-graph__tooltip-hint">Tap again to open</div>
        ) : null;
        if (n.kind === 'theme') {
            return (
                <div
                    className="focus-graph__tooltip"
                    role="tooltip"
                    style={positionStyle}
                    {...touchAttr}
                >
                    <div className="focus-graph__tooltip-n">Theme</div>
                    {lang === 'ta' ? (
                        <>
                            <div lang="ta" className="focus-graph__tooltip-lead">
                                {n.title_ta}
                            </div>
                            <div className="focus-graph__tooltip-sub">{n.title_en}</div>
                        </>
                    ) : (
                        <>
                            <div className="focus-graph__tooltip-lead">{n.title_en}</div>
                            <div lang="ta" className="focus-graph__tooltip-sub">
                                {n.title_ta}
                            </div>
                        </>
                    )}
                    {hint}
                </div>
            );
        }
        return (
            <div
                className="focus-graph__tooltip"
                role="tooltip"
                style={positionStyle}
                {...touchAttr}
            >
                <div className="focus-graph__tooltip-n">Kural {n.kural_n}</div>
                <div lang="ta" className="focus-graph__tooltip-lead">
                    {(n.verse_ta && n.verse_ta.length > 0
                        ? n.verse_ta
                        : [n.first_line_ta ?? '']
                    ).map((line, i) => (
                        <span key={i} className="focus-graph__tooltip-verse-line">
                            {line}
                        </span>
                    ))}
                </div>
                <div className="focus-graph__tooltip-sub">{n.gist_en}</div>
                {hint}
            </div>
        );
    }
    if (payload.edge?.shared_tags) {
        const themeBySlug = new Map(graph.themes.map((t) => [t.slug, t]));
        return (
            <div
                className="focus-graph__tooltip"
                role="tooltip"
                style={positionStyle}
                {...touchAttr}
            >
                <div className="focus-graph__tooltip-n">Shared themes</div>
                <div>
                    {payload.edge.shared_tags
                        .map((slug) => {
                            const t = themeBySlug.get(slug);
                            if (!t) return slug;
                            return lang === 'ta' ? t.title_ta : t.title_en;
                        })
                        .join(' · ')}
                </div>
            </div>
        );
    }
    return null;
}
